import apiService from './api'

export interface GitHubRepository {
  id: number
  name: string
  full_name: string
  owner: {
    login: string
    avatar_url: string
  }
  description: string | null
  private: boolean
  html_url: string
  clone_url: string
  default_branch: string
  language: string | null
  stargazers_count: number
  forks_count: number
  updated_at: string
  archived: boolean
  disabled: boolean
}

export interface GitHubUser {
  login: string
  id: number
  avatar_url: string
  name: string | null
  email: string | null
  company: string | null
  location: string | null
  bio: string | null
  public_repos: number
  private_repos?: number
  total_private_repos?: number
}

export interface GitHubOrganization {
  login: string
  id: number
  avatar_url: string
  description: string | null
  name: string | null
  email: string | null
  blog: string | null
  location: string | null
  public_repos: number
}

export interface GitHubRepositoryInventory {
  full_name: string
  default_branch: string
  manifest_files: string[]
  manifest_types: string[]
  has_scan_targets: boolean
  error?: string | null
}

class GitHubService {
  /**
   * Fetch user's GitHub repositories
   * This will call our backend which uses the user's GitHub OAuth token
   */
  async getUserRepositories(params?: {
    page?: number
    per_page?: number
    sort?: 'created' | 'updated' | 'pushed' | 'full_name'
    direction?: 'asc' | 'desc'
    type?: 'all' | 'owner' | 'public' | 'private' | 'member'
  }): Promise<GitHubRepository[]> {
    return await apiService.get<GitHubRepository[]>('/github/repositories', { params })
  }

  /**
   * Fetch user's GitHub organizations
   */
  async getUserOrganizations(params?: {
    page?: number
    per_page?: number
  }): Promise<GitHubOrganization[]> {
    return await apiService.get<GitHubOrganization[]>('/github/organizations', { params })
  }

  /**
   * Fetch repositories for a specific organization
   */
  async getOrganizationRepositories(
    org: string,
    params?: {
      page?: number
      per_page?: number
      type?: 'all' | 'public' | 'private' | 'forks' | 'sources' | 'member'
    }
  ): Promise<GitHubRepository[]> {
    return await apiService.get<GitHubRepository[]>(
      `/github/organizations/${org}/repositories`,
      { params }
    )
  }

  /**
   * Fetch user's GitHub profile
   */
  async getUserProfile(): Promise<GitHubUser> {
    return await apiService.get<GitHubUser>('/github/user')
  }

  /**
   * Search repositories (public only for now)
   */
  async searchRepositories(query: string): Promise<GitHubRepository[]> {
    return await apiService.get<GitHubRepository[]>('/github/search', {
      params: { q: query }
    })
  }

  /**
   * Get repository details
   */
  async getRepository(owner: string, repo: string): Promise<GitHubRepository> {
    return await apiService.get<GitHubRepository>(`/github/repos/${owner}/${repo}`)
  }

  async inspectRepositories(repositories: string[]): Promise<GitHubRepositoryInventory[]> {
    return await apiService.post<GitHubRepositoryInventory[]>('/github/inventory', { repositories })
  }
}

export default new GitHubService()
